import { useEffect, useState, type FormEvent } from 'react';
import { Button, Input, Modal } from '../../components';
import { ApiError, apiRequest } from '../../lib/api';

export type ChangePasswordModalProps = {
  open: boolean;
  onClose: () => void;
};

const MIN_LEN = 12;

export function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrent('');
      setNext('');
      setConfirm('');
      setSubmitting(false);
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (next.length < MIN_LEN) {
      setError(`New password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (next !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (next === current) {
      setError('New password must differ from current password.');
      return;
    }

    setSubmitting(true);
    try {
      await apiRequest('/auth/change-password', {
        method: 'POST',
        body: { current_password: current, new_password: next },
      });
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Change password" width={460}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {success ? (
            <div className="muted" style={{ fontSize: 13 }}>
              Your password has been updated. Other sessions on different devices have been signed
              out.
            </div>
          ) : (
            <>
              <div className="muted" style={{ fontSize: 12 }}>
                For your security, we'll keep you signed in here, but other devices will be
                required to sign in again.
              </div>
              <Input
                id="cp-current"
                label="Current password"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={setCurrent}
                required
                autoFocus
              />
              <Input
                id="cp-new"
                label={`New password (min ${MIN_LEN} chars)`}
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={setNext}
                minLength={MIN_LEN}
                required
              />
              <Input
                id="cp-confirm"
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={setConfirm}
                minLength={MIN_LEN}
                required
              />
            </>
          )}
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{
            justifyContent: 'flex-end',
            borderTop: '1px solid var(--border)',
            borderBottom: 'none',
          }}
        >
          {success ? (
            <Button variant="primary" type="button" onClick={onClose}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={onClose} disabled={submitting} type="button">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? 'Updating…' : 'Update password'}
              </Button>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}
